const variables = {
    windowInnerWidth: '--window-inner-width',
    headerHeight: '--header-height',
    headerWrapperBottomPosition: '--header-wrapper-bottom-position',
    headerNavigationHeight: '--header-navigation-height',
};

const selectors = {
    headerWrapper: '.js-header-wrapper',
    headerNavigation: '.js-header-navigation',
    sideMenu: {
        id: 'side-menu',
        openers: '.js-side-menu-open',
        closures: '.js-side-menu-close'
    },
    sideDrawerDefaultSelectors: {
        scrollWrapper: '.js-side-drawer-scroll-wrapper',
        openers: '.js-side-drawer-open',
        closures: '.js-side-drawer-close',
        offCanvasContainer: '.js-off-canvas'
    },
    submenuDefaultSelectors: {
        submenu: '.js-submenu',
        submenuWrapper: '.js-submenu-wrapper',
        submenuOpeners: '.js-open-submenu',
        submenuClosures: '.js-close-submenu',
        scrollWrapper: '.js-scroll-wrapper'
    }
};

const modifiers = {
    isMenuOpened: 'is-opened',
    bodyIsSideMenuOpened: 'is-side-menu-opened',
    isSubmenuOpened: 'is-opened',
    submenuWithoutTransition: 'is-without-transition'
};

class HeaderComponent extends HTMLElement {
    constructor() {
        super()
        this.container = this;
        this.headerWrapper = this.container.querySelector(selectors.headerWrapper);
        this.headerNavigation = this.container.querySelector(selectors.headerNavigation);

        this.windowWidth = null;

        this.variables = Object.values(variables).reduce((result, variable) => {
            return {
                ...result,
                [variable]: 0,
            };
        }, {});

        const sideMenuContainer = document.getElementById(selectors.sideMenu.id);

        this.sideMenu = new SideDrawer(sideMenuContainer, {
            openers: selectors.sideMenu.openers,
            closures: selectors.sideMenu.closures
        }, true);

        this.initHeader();
    }

    initHeader() {
        this.attachListeners();

        this.onResize();
        this.onScroll();
    }

    attachListeners() {
        /*
        * Throttling is not needed here, because it is not an expensive computation
        */

        window.addEventListener('resize', this.onResize.bind(this));
        window.addEventListener('scroll', this.onScroll.bind(this));
    }

    setWindowInnerWidthVariable() {
        const bodyClientWidth = document.body.clientWidth;

        this.variables[variables.windowInnerWidth] = bodyClientWidth;

        document.documentElement.style.setProperty(variables.windowInnerWidth, `${bodyClientWidth}px`);
    }

    setVariable(variable, value, unit = 'px') {
        this.variables[variable] = value;

        document.documentElement.style.setProperty(variable, `${value}${unit}`);
    }

    getHeaderWrapperBottomPosition() {
        const rect = this.headerWrapper.getBoundingClientRect();

        return Math.round(rect.bottom * 100) / 100;
    }

    getHeaderHeight() {
        return this.headerWrapper.getBoundingClientRect().height;
    }

    getHeaderNavigationHeight() {
        if (!this.headerNavigation) {
            return 0;
        }

        return this.headerNavigation.getBoundingClientRect().height;
    }

    setHeaderWrapperBottomPositionVariable() {
        this.setVariable(variables.headerWrapperBottomPosition, this.getHeaderWrapperBottomPosition());

        this.setVariable(variables.headerHeight, this.getHeaderHeight());

        this.setVariable(variables.headerNavigationHeight, this.getHeaderNavigationHeight());
    }

    getVariable(variableKey) {
        return this.variables[variableKey];
    }

    onResize() {
        if (window.innerWidth === this.windowWidth) {
            return null;
        }

        this.windowWidth = window.innerWidth;

        this.setWindowInnerWidthVariable();
        this.setHeaderWrapperBottomPositionVariable();
    }

    onScroll() {
        this.setHeaderWrapperBottomPositionVariable();
    }
}

class SideDrawer {
    constructor(container, customSelectors = {}, isWithOffCanvas = false) {
        this.container = container;

        this.selectors = {
            ...selectors.sideDrawerDefaultSelectors,
            ...customSelectors
        };

        this.isWithOffCanvas = isWithOffCanvas;

        this.isOpened = false;

        this.init();
    }

    init() {
        if (!this.container) {
            return null;
        }

        this.scrollWrapper = null;

        this.attachListeners();

        if (!this.isWithOffCanvas) {
            return null;
        }

        const offCanvasContainer = this.container.querySelector(this.selectors.offCanvasContainer);
        this.offCanvas = new OffCanvas(offCanvasContainer, this.lockScroll.bind(this), {
            scrollWrapper: this.selectors.scrollWrapper,
        });
    }

    open() {
        this.isOpened = true;

        if (this.offCanvas) {
            this.offCanvas.closeSubmenus();
        }

        this.scrollWrapper = this.getMainScrollWrapper();

        this.scrollWrapper.scrollTop = 0;

        this.container.classList.add(modifiers.isMenuOpened);

        document.body.classList.add(modifiers.bodyIsSideMenuOpened);
    }

    getMainScrollWrapper() {
        return this.container.querySelector(this.selectors.scrollWrapper);
    }

    close() {
        this.isOpened = false;

        this.container.classList.remove(modifiers.isMenuOpened);

        document.body.classList.remove(modifiers.bodyIsSideMenuOpened);
    }

    lockScroll(scrollWrapper) {
        bodyScrollLock.enableBodyScroll(this.scrollWrapper);
        bodyScrollLock.disableBodyScroll(scrollWrapper);

        this.scrollWrapper = scrollWrapper;
    }

    attachListeners() {
        if (this.selectors.closures) {
            document.documentElement.customDelegate('click', this.selectors.closures, this.close.bind(this));
        }

        if (this.selectors.openers) {
            document.documentElement.customDelegate('click', this.selectors.openers, this.open.bind(this));
        }
    }
}

class OffCanvas {
    constructor(container, lockScrollCallback = () => {}, customSelectors = {}) {
        this.container = container;

        this.lockScrollCallback = lockScrollCallback;
        this.selectors = {
            ...selectors.submenuDefaultSelectors,
            ...customSelectors,
        };

        this.stackOpenSubmenus = [];

        this.init();
    }

    init() {
        if (!this.container) {
            return null;
        }

        this.attachListeners();
    }

    attachListeners() {
        this.container.customDelegate('click', this.selectors.submenuOpeners, this.handleSubmenuOpen.bind(this));
        this.container.customDelegate('click', this.selectors.submenuClosures, this.handleSubmenuClose.bind(this));
    }

    handleSubmenuOpen(e) {
        const target = e.target;

        const submenuWrapper = target.closest(this.selectors.submenuWrapper);

        if (!submenuWrapper) {
            return null;
        }

        const submenu = submenuWrapper.querySelector(this.selectors.submenu);

        if (!submenu) {
            return null;
        }

        submenu.classList.add(modifiers.isSubmenuOpened);

        this.stackOpenSubmenus.push(submenu);

        e.preventDefault();
    }

    handleSubmenuClose(e) {
        const target = e.target;

        const submenu = target.closest(this.selectors.submenu);

        if (!submenu) {
            return null;
        }

        submenu.classList.remove(modifiers.isSubmenuOpened);

        this.stackOpenSubmenus.pop();

        e.preventDefault();
    }

    closeSubmenus() {
        this.stackOpenSubmenus.forEach(submenu => {
            submenu.classList.add(modifiers.submenuWithoutTransition);
            submenu.classList.remove(modifiers.isSubmenuOpened);

            requestAnimationFrame(() => {
                submenu.classList.remove(modifiers.submenuWithoutTransition);
            });
        });
    }
}

customElements.define('main-header', HeaderComponent );